import { render, renderNav, showToast, showApiError, refreshChatsCount } from '../main';
import {
  API_ERR_LOAD_CHAT, API_ERR_ICEBREAKER, API_ERR_SEND_MESSAGE,
  API_ERR_CLEAR_CHAT, API_ERR_DELETE_PHOTOS, API_ERR_UNTRUST,
  TOAST_FAILED_ACCEPT_MESSAGE, TOAST_FAILED_REJECT_MESSAGE, TOAST_FAILED_UPLOAD_IMAGE,
} from '../utils/app-strings';
import { chatsApi } from '../api';
import { navigate, getCurrentPath, extractParams } from '../router';
import { store, Message, MessageImage } from '../state';
import socket from '../socket';
import { TYPING_TIMEOUT_MS, AI_MATCHMAKER_USER_ID } from '../config';
import { escapeHtml, loadingSpinner } from '../utils';
import { loadAnswersTab, cleanupAnswersTab, setChatId as setAnswersChatId } from './chat-answers';
import { updateChatBreadcrumb } from './chat-breadcrumb';
import { openImageViewer } from '../components/image-viewer';
import { renderUploadButton, setupUploadButton } from '../components/upload-button';
import type { SentImage } from '../types/api';

const QA_REPLY_RE = /^\{\{QA_REPLY\|(\d+)\|(.+?)\|([^|]+)\|([^}]+)\}\}([\s\S]*)$/;

/** Append the nav bar to #app (for answers tab) */
function showChatNav(): void {
  if (document.querySelector('.navbar')) return; // already present
  const app = document.getElementById('app');
  if (app) app.insertAdjacentHTML('beforeend', renderNav());
  document.querySelector('.chat-container')?.classList.add('has-nav');
}

/** Remove the nav bar (for chat tab - input needs full viewport) */
function hideChatNav(): void {
  document.querySelector('.navbar')?.remove();
  document.querySelector('.chat-container')?.classList.remove('has-nav');
}

let chatId: string = '';
let otherUserId: string = '';
let otherUserName: string = '';
let otherUserAvatar: string | null = null;
let otherUserTyping: string = '';
let aiThinking: boolean = false;
let aiThinkingUserId: string = '';
let aiThinkingTimeoutId: number | null = null;
let aiUploading: boolean = false;
let aiUploadingTimeoutId: number | null = null;
let messages: Message[] = [];
let typingTimeoutId: number | null = null;
let answersTabLoaded = false;

// Image picker state
let imagePickerOpen = false;
let sentImages: SentImage[] = [];
let selectedMediaIds = new Set<string>();
let pickerUploadCleanup: (() => void) | undefined;

export async function renderChat(): Promise<void> {
  const path = getCurrentPath();

  // Try most-specific route first to extract chatId and optional setId
  let params = extractParams('/chat/:id/answers/:setId', path);
  let initialSetId: string | null = params.setId || null;
  if (!params.id) params = extractParams('/chat/:id/answers', path);
  if (!params.id) params = extractParams('/chat/:id', path);
  chatId = params.id;

  const isAnswersTab = path.includes('/answers');

  // Clean up any stale answers state from a previous chat (e.g. navigating between chats)
  cleanupAnswersTab();
  answersTabLoaded = false;
  imagePickerOpen = false;

  // Pass chatId to answers module for URL updates
  setAnswersChatId(chatId);

  render(`
    <div class="view-frame chat-container">
      <!-- Row 1: Header (person info + sort when on answers tab) -->
      <div class="chat-header">
        <button class="chat-back" id="back-btn">
          <i class="fas fa-arrow-left"></i>
        </button>
        <div class="chat-title">Loading...</div>
        <div id="answers-sort-header" style="display: none;"></div>
      </div>
      <div id="answers-sort-menu-slot"></div>

      <!-- Row 2: Breadcrumb-tabs -->
      <div class="chat-breadcrumb" id="chat-breadcrumb">
        <div class="seg">
          <button class="seg-btn ${isAnswersTab ? 'active' : ''}" data-nav="answers">Chat Packs</button>
          <button class="seg-btn ${!isAnswersTab ? 'active' : ''}" data-nav="chat">Chat</button>
        </div>
        <span class="chat-crumb-sep chat-crumb-leaf-sep" style="display:none">&rsaquo;</span>
        <span class="chat-crumb-leaf" style="display:none"></span>
      </div>

      <!-- Tab content: Chat -->
      <div class="chat-tab-content ${!isAnswersTab ? 'active' : ''}" id="tab-chat">
        <div class="view-scroll chat-messages" id="messages">
          ${loadingSpinner()}
        </div>
        <div class="chat-input-container">
          <input
            type="text"
            class="input chat-input"
            id="message-input"
            placeholder="Type a message..."
            disabled
          />
          <button class="btn btn-primary chat-send" id="send-btn" disabled>
            <i class="fas fa-paper-plane"></i>
          </button>
          <button class="btn btn-outline chat-photo-btn" id="photo-btn" title="Send photo" disabled>
            <i class="fas fa-camera"></i>
          </button>
        </div>
        <!-- Image picker overlay -->
        <div class="chat-image-picker" id="image-picker" style="display:none"></div>
      </div>

      <!-- Tab content: Answers -->
      <div class="chat-tab-content ${isAnswersTab ? 'active' : ''}" id="tab-answers">
        <div class="view-scroll" id="answers-content"></div>
      </div>
    </div>
  `, false);

  // Show/hide nav based on initial tab
  if (isAnswersTab) showChatNav();

  document.getElementById('back-btn')!.addEventListener('click', () => {
    navigate('/matches');
  });

  // Breadcrumb-tabs navigation (event delegation)
  const breadcrumb = document.getElementById('chat-breadcrumb');
  const contents = document.querySelectorAll('.chat-tab-content');

  breadcrumb?.addEventListener('click', (e) => {
    const crumb = (e.target as HTMLElement).closest<HTMLElement>('.seg-btn');
    if (!crumb) return;

    const nav = crumb.dataset.nav;
    if (!nav) return;

    if (nav === 'chat') {
      // Switch to messages tab
      contents.forEach(c => c.classList.remove('active'));
      document.getElementById('tab-chat')?.classList.add('active');
      // Hide answers sort button
      const sortHeader = document.getElementById('answers-sort-header');
      const sortMenuSlot = document.getElementById('answers-sort-menu-slot');
      if (sortHeader) sortHeader.style.display = 'none';
      if (sortMenuSlot) sortMenuSlot.innerHTML = '';
      hideChatNav();
      history.replaceState(null, '', `#/chat/${chatId}`);
      updateChatBreadcrumb();
    } else if (nav === 'answers') {
      // Always go to set list (even if already on answers)
      contents.forEach(c => c.classList.remove('active'));
      document.getElementById('tab-answers')?.classList.add('active');
      showChatNav();
      history.replaceState(null, '', `#/chat/${chatId}/answers`);

      if (answersTabLoaded) {
        loadAnswersTab(otherUserId, otherUserName);
      } else if (otherUserId) {
        answersTabLoaded = true;
        loadAnswersTab(otherUserId, otherUserName);
      }
      updateChatBreadcrumb();
    }
  });

  await loadChat();
  setupSocketListeners();
  setupInputHandlers();
  setupQuoteLinkHandlers();
  setupImageClickHandlers();
  updateChatBreadcrumb();

  // If URL points to answers tab, load it now that we have otherUserId
  if (isAnswersTab && otherUserId) {
    answersTabLoaded = true;
    await loadAnswersTab(otherUserId, otherUserName, initialSetId || undefined);
  }
}

async function loadChat(): Promise<void> {
  try {
    // Load chat details
    const chatRes = await chatsApi.getById(chatId);
    const chat = chatRes.data;

    const userId = store.getState().user?.id;
    otherUserId = chat.user_id_initiated === userId
      ? chat.user_id_recipient
      : chat.user_id_initiated;
    otherUserName = chat.other_user_name;
    otherUserAvatar = chat.other_user_avatar;

    // Update header with avatar, name, and profile link
    const avatarHtml = otherUserAvatar
      ? `<img src="${otherUserAvatar}" alt="${otherUserName}" />`
      : `<i class="fas fa-user"></i>`;

    document.querySelector('.chat-title')!.innerHTML = `
      <a href="#/profile/${otherUserId}" class="chat-profile-link" title="View profile">
        <div class="avatar avatar-md chat-avatar">
          ${avatarHtml}
          <div class="chat-avatar-overlay">
            <span class="chat-avatar-name">${otherUserName}</span>
          </div>
          <span class="chat-avatar-arrow"><i class="fas fa-external-link-alt"></i></span>
        </div>
      </a>
    `;

    // Load messages
    const messagesRes = await chatsApi.getMessages(chatId);
    messages = messagesRes.data;

    renderMessages();

    // Enable input
    const input = document.getElementById('message-input') as HTMLInputElement;
    input.disabled = false;
    input.focus();

    // Enable photo button
    const photoBtn = document.getElementById('photo-btn') as HTMLButtonElement;
    photoBtn.disabled = false;

    // Join chat room
    socket.joinChat(chatId);

    // Log this view for unread tracking and refresh badge
    chatsApi.logView(chatId).then(() => refreshChatsCount()).catch(() => {});

  } catch (err: any) {
    showApiError(err, API_ERR_LOAD_CHAT);
    navigate('/matches');
  }
}

function renderMessageContent(content: string): string {
  // Strip {{IMG|...}} tags (invisible to humans - used for AI context only)
  const stripped = content.replace(/\{\{IMG\|[^}]+\}\}/g, '');
  if (!stripped) return ''; // Image-only message - no text content

  const m = QA_REPLY_RE.exec(stripped);
  if (m) {
    const [, num, answerText, setId, questionId, replyText] = m;
    const quoteInner = `<span class="msg-qa-quote-label">Q${escapeHtml(num)}:</span> ${escapeHtml(answerText)}`;
    return `<a class="msg-qa-quote msg-qa-quote-link" data-set-id="${escapeHtml(setId)}" data-question-id="${escapeHtml(questionId)}">${quoteInner}</a>${escapeHtml(replyText)}`;
  }
  return escapeHtml(stripped);
}

function renderMessageImages(images: MessageImage[]): string {
  if (!images || images.length === 0) return '';
  const thumbs = images.map((img, i) =>
    `<img class="message-image-thumb" src="${img.thumb_url}" data-large-url="${img.large_url}" data-img-index="${i}" data-msg-images='${JSON.stringify(images.map(im => im.large_url))}' />`
  ).join('');
  return `<div class="message-images">${thumbs}</div>`;
}

function renderMessages(): void {
  const container = document.getElementById('messages')!;
  const userId = store.getState().user?.id;

  if (messages.length === 0 && !otherUserTyping && !aiThinking) {
    container.innerHTML = `
      <div class="empty-state">
        <p>Say hello to ${otherUserName}!</p>
      </div>
    `;
    return;
  }

  let html = messages.map(msg => renderSingleMessage(msg, userId)).join('');

  // Append the live indicator zone (typing bubble / AI thinking)
  html += renderLiveIndicator();

  container.innerHTML = html;
  wireApprovalButtons(container);
  container.scrollTop = container.scrollHeight;
}

/** Render a single message bubble (extracted so renderMessages + appendNewMessage share it). */
function renderSingleMessage(msg: Message, userId: string | undefined): string {
  const isSent = msg.sender_id === userId;
  const isMatchmaker = msg.sender_id === AI_MATCHMAKER_USER_ID;
  const isPending = msg.delivery_status === 'pending_approval' && !isSent;
  const isRejected = msg.delivery_status === 'rejected' && isSent;
  const time = new Date(msg.created_at).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });

  const imagesHtml = renderMessageImages(msg.images || []);
  const renderedContent = msg.content ? renderMessageContent(msg.content) : '';
  const contentHtml = renderedContent ? `<div>${renderedContent}</div>` : '';

  if (isMatchmaker) {
    return `
      <div class="message message-matchmaker" data-msg-id="${msg.id}">
        <div class="matchmaker-label">Ai MatchMaker</div>
        ${contentHtml}
        ${imagesHtml}
        <div class="message-time">${time}</div>
      </div>
    `;
  }

  if (isPending) {
    return renderApprovalCard(msg, time);
  }

  const rejectedClass = isRejected ? ' message-rejected' : '';
  const rejectedPill = isRejected ? '<span class="rejected-pill">Rejected</span>' : '';

  return `
    <div class="message ${isSent ? 'message-sent' : 'message-received'}${rejectedClass}" data-msg-id="${msg.id}">
      ${rejectedPill}
      ${contentHtml}
      ${imagesHtml}
      <div class="message-time">${time}</div>
    </div>
  `;
}

/** Render the approval card for a pending_approval message. */
function renderApprovalCard(msg: Message, time: string): string {
  const hasImages = msg.pending_image_info && msg.pending_image_info.length > 0;
  const imageCount = hasImages ? msg.pending_image_info!.length : 0;

  // Header for image messages
  const headerHtml = hasImages
    ? `<div class="approval-header">${escapeHtml(otherUserName)} wants to share ${imageCount} image${imageCount !== 1 ? 's' : ''}</div>`
    : '';

  // AI captions (for images only)
  let captionsHtml = '';
  if (hasImages) {
    const captions = msg.pending_image_info!
      .filter(info => info.caption_ai)
      .map(info => `<div class="approval-caption">"${escapeHtml(info.caption_ai!)}"</div>`);
    captionsHtml = captions.join('');
  }

  // Moderation summary
  const moderationHtml = renderModerationSummary(msg.moderation_summary);

  // Text preview for text-only flagged messages
  const textPreviewHtml = !hasImages && msg.content
    ? `<div class="approval-text-preview">Message may contain flagged content</div>`
    : '';

  return `
    <div class="message message-received message-approval" data-msg-id="${msg.id}">
      <div class="approval-card">
        ${headerHtml}
        ${captionsHtml}
        ${textPreviewHtml}
        ${moderationHtml}
        <div class="approval-actions">
          <button class="btn btn-sm btn-outline approval-reject-btn" data-msg-id="${msg.id}">Reject</button>
          <button class="btn btn-sm btn-primary approval-accept-btn" data-msg-id="${msg.id}">Accept</button>
          <button class="btn btn-sm btn-primary approval-always-btn" data-msg-id="${msg.id}">Always Accept</button>
        </div>
      </div>
      <div class="message-time">${time}</div>
    </div>
  `;
}

/** Render moderation summary pills or "Moderation passed". */
function renderModerationSummary(summary: Record<string, number> | null | undefined): string {
  if (!summary || Object.keys(summary).length === 0) {
    return `<div class="approval-moderation-pass"><i class="fas fa-check-circle"></i> Moderation passed</div>`;
  }

  const pills = Object.entries(summary).map(([label, score]) => {
    const pct = Math.round(score * 100);
    let colorClass = 'moderation-low';
    if (pct >= 80) colorClass = 'moderation-high';
    else if (pct >= 50) colorClass = 'moderation-medium';
    return `<span class="moderation-pill ${colorClass}">${escapeHtml(label)} ${pct}%</span>`;
  }).join(' ');

  return `<div class="approval-moderation-flagged">${pills}</div>`;
}

/** Build the HTML for the live indicator zone (AI thinking dots, typing bubble, or upload progress). */
function renderLiveIndicator(): string {
  let html = '';

  if (aiThinking && !otherUserTyping) {
    if (aiThinkingUserId === AI_MATCHMAKER_USER_ID) {
      html = `
        <div class="message message-matchmaker">
          <div class="matchmaker-label">Ai MatchMaker</div>
          <div><span class="thinking-dots"></span></div>
        </div>
      `;
    } else {
      html = `
        <div class="ai-thinking">
          <span class="thinking-dots"></span>
        </div>
      `;
    }
  }

  if (otherUserTyping) {
    html = `
      <div class="message message-received">
        <div>${escapeHtml(otherUserTyping)}</div>
      </div>
    `;
  }

  if (aiUploading && !otherUserTyping) {
    html += `
      <div class="ai-uploading">
        <div class="ai-uploading-label">Uploading</div>
        <div class="upload-progress-track">
          <div class="upload-progress-fill ai-upload-indeterminate"></div>
        </div>
      </div>
    `;
  }

  return `<div id="live-indicator">${html}</div>`;
}

/**
 * Surgically update only the live indicator zone (typing / AI thinking).
 * Avoids a full innerHTML rebuild so existing message images don't flash.
 */
function updateLiveIndicator(): void {
  const zone = document.getElementById('live-indicator');
  if (!zone) {
    // Fallback: full re-render if zone doesn't exist yet (e.g. empty state)
    renderMessages();
    return;
  }

  zone.innerHTML = renderLiveIndicator().replace(/^<div id="live-indicator">/, '').replace(/<\/div>$/, '');

  // Keep scroll pinned to bottom
  const container = document.getElementById('messages');
  if (container) container.scrollTop = container.scrollHeight;
}

/**
 * Append a new message to the DOM without re-rendering the whole list.
 * Falls back to full renderMessages() if the container isn't in the expected state.
 */
function appendMessage(msg: Message): void {
  const container = document.getElementById('messages');
  const zone = document.getElementById('live-indicator');
  if (!container || !zone) {
    renderMessages();
    return;
  }

  // Remove empty-state if present
  const empty = container.querySelector('.empty-state');
  if (empty) empty.remove();

  const userId = store.getState().user?.id;
  const msgHtml = renderSingleMessage(msg, userId);

  // Insert the new message before the live indicator zone
  zone.insertAdjacentHTML('beforebegin', msgHtml);

  // Wire approval buttons on the newly inserted message only
  const newMsgEl = zone.previousElementSibling;
  if (newMsgEl) wireApprovalButtons(newMsgEl as HTMLElement);

  // Update the live indicator (clear typing, clear AI thinking)
  updateLiveIndicator();
}

/** Wire up accept/reject buttons on approval cards. */
function wireApprovalButtons(container: HTMLElement): void {
  container.querySelectorAll('.approval-accept-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const msgId = (btn as HTMLElement).dataset.msgId;
      if (!msgId) return;
      (btn as HTMLButtonElement).disabled = true;
      try {
        await chatsApi.messageAction(chatId, msgId, 'accept');
        // The message:approved socket event will replace the card
      } catch {
        (btn as HTMLButtonElement).disabled = false;
        showToast(TOAST_FAILED_ACCEPT_MESSAGE, 'error');
      }
    });
  });
  container.querySelectorAll('.approval-always-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const msgId = (btn as HTMLElement).dataset.msgId;
      if (!msgId) return;
      (btn as HTMLButtonElement).disabled = true;
      try {
        await chatsApi.messageAction(chatId, msgId, 'accept');
        chatsApi.setImageTrust(chatId, true).catch(() => {});
      } catch {
        (btn as HTMLButtonElement).disabled = false;
        showToast(TOAST_FAILED_ACCEPT_MESSAGE, 'error');
      }
    });
  });
  container.querySelectorAll('.approval-reject-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const msgId = (btn as HTMLElement).dataset.msgId;
      if (!msgId) return;
      (btn as HTMLButtonElement).disabled = true;
      try {
        await chatsApi.messageAction(chatId, msgId, 'reject');
        // Remove the card from DOM
        const msgEl = document.querySelector(`.message-approval[data-msg-id="${msgId}"]`);
        if (msgEl) msgEl.remove();
        // Remove from local messages array
        messages = messages.filter(m => m.id !== msgId);
      } catch {
        (btn as HTMLButtonElement).disabled = false;
        showToast(TOAST_FAILED_REJECT_MESSAGE, 'error');
      }
    });
  });
}

// Click handler for image thumbnails in messages -> open fullscreen viewer
function setupImageClickHandlers(): void {
  const container = document.getElementById('messages');
  if (!container) return;

  container.addEventListener('click', (e) => {
    const thumb = (e.target as HTMLElement).closest<HTMLImageElement>('.message-image-thumb');
    if (!thumb) return;

    const urlsAttr = thumb.dataset.msgImages;
    const index = parseInt(thumb.dataset.imgIndex || '0', 10);
    if (urlsAttr) {
      try {
        const urls = JSON.parse(urlsAttr) as string[];
        openImageViewer(urls, index);
      } catch {
        openImageViewer(thumb.dataset.largeUrl || thumb.src);
      }
    }
  });
}

// Helper to clear AI thinking timeout
const clearAiThinkingTimeout = () => {
  if (aiThinkingTimeoutId) {
    clearTimeout(aiThinkingTimeoutId);
    aiThinkingTimeoutId = null;
  }
};

// Helper to clear AI uploading timeout
const clearAiUploadingTimeout = () => {
  if (aiUploadingTimeoutId) {
    clearTimeout(aiUploadingTimeoutId);
    aiUploadingTimeoutId = null;
  }
};

function setupSocketListeners(): void {
  // AI thinking indicator (shows "..." before typing starts)
  socket.onAiThinking((data) => {
    if (data.chatId === chatId) {
      aiThinking = true;
      aiThinkingUserId = data.aiUserId || '';
      clearAiThinkingTimeout();
      aiThinkingTimeoutId = window.setTimeout(() => {
        aiThinking = false;
        updateLiveIndicator();
      }, 15000);
      updateLiveIndicator();
    }
  });

  // AI uploading indicator (shows progress bar while photo is being generated)
  socket.onAiUploading((data) => {
    if (data.chatId === chatId) {
      aiUploading = true;
      clearAiUploadingTimeout();
      aiUploadingTimeoutId = window.setTimeout(() => {
        aiUploading = false;
        updateLiveIndicator();
      }, 60000);
      updateLiveIndicator();
    }
  });

  socket.onAiUploadingDone((data) => {
    if (data.chatId === chatId) {
      aiUploading = false;
      clearAiUploadingTimeout();
      updateLiveIndicator();
    }
  });

  // New message received
  socket.onNewMessage((data) => {
    if (data.chatId === chatId) {
      const hasImages = data.message.images && data.message.images.length > 0;
      const exists = messages.some(m => m.id === data.message.id);
      if (exists) {
        // Duplicate socket event (chat room + personal room) - just clear indicators
        otherUserTyping = '';
        aiThinking = false;
        clearAiThinkingTimeout();
        if (hasImages) { aiUploading = false; clearAiUploadingTimeout(); }
        updateLiveIndicator();
        return;
      }

      const userId = store.getState().user?.id;

      // If this is our own message coming back from the server, replace the optimistic one
      if (data.message.sender_id === userId) {
        const pendingIdx = messages.findIndex(
          m => m.id.startsWith('pending_') && m.content === data.message.content
        );
        if (pendingIdx !== -1) {
          const oldId = messages[pendingIdx].id;
          messages[pendingIdx] = data.message;
          // Update DOM data-msg-id so subsequent events (e.g. message:rejected) can find it
          const el = document.querySelector(`[data-msg-id="${oldId}"]`);
          if (el) el.setAttribute('data-msg-id', data.message.id);
          otherUserTyping = '';
          aiThinking = false;
          clearAiThinkingTimeout();
          updateLiveIndicator();
          return;
        }
      }

      messages.push(data.message);
      otherUserTyping = '';
      aiThinking = false;
      clearAiThinkingTimeout();
      if (hasImages) { aiUploading = false; clearAiUploadingTimeout(); }
      appendMessage(data.message);

      if (data.message.sender_id !== userId) {
        chatsApi.logView(chatId).then(() => refreshChatsCount()).catch(() => {});
      }
    }
  });

  // Live typing (works for both humans and AI)
  socket.onTyping((data) => {
    if (data.chatId === chatId && data.userId === otherUserId) {
      otherUserTyping = data.text;
      updateLiveIndicator();
    }
  });

  // Message approved - replace approval card with full message
  socket.onMessageApproved((data) => {
    if (data.chatId === chatId) {
      const idx = messages.findIndex(m => m.id === data.messageId);
      if (idx !== -1) {
        messages[idx] = data.message;
      }
      // Replace the approval card DOM element
      const cardEl = document.querySelector(`.message-approval[data-msg-id="${data.messageId}"]`);
      if (cardEl) {
        const userId = store.getState().user?.id;
        const html = renderSingleMessage(data.message, userId);
        cardEl.insertAdjacentHTML('afterend', html);
        // Wire image click handlers on new element
        const newEl = cardEl.nextElementSibling;
        if (newEl) {
          newEl.querySelectorAll('.message-image-thumb').forEach((thumb) => {
            // Image clicks are handled by the delegated listener on #messages
          });
        }
        cardEl.remove();
      }
    }
  });

  // Message rejected by AI auto-reject
  socket.onMessageRejected((data) => {
    if (data.chatId === chatId) {
      const idx = messages.findIndex(m => m.id === data.messageId);
      if (idx !== -1) {
        messages[idx].delivery_status = 'rejected';
      }
      const msgEl = document.querySelector(`[data-msg-id="${data.messageId}"]`) as HTMLElement | null;
      if (msgEl) {
        msgEl.classList.add('message-rejected');
        if (!msgEl.querySelector('.rejected-pill')) {
          msgEl.insertAdjacentHTML('afterbegin', '<span class="rejected-pill">Rejected</span>');
        }
      }
    }
  });

  // Chat cleared - other user (or this user from another tab) cleared all messages
  socket.onChatCleared((data) => {
    if (data.chatId === chatId) {
      messages = [];
      renderMessages();
    }
  });
}

function setupQuoteLinkHandlers(): void {
  const container = document.getElementById('messages');
  if (!container) return;

  container.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest<HTMLElement>('.msg-qa-quote-link');
    if (!link) return;
    e.preventDefault();

    const setId = link.dataset.setId;
    const questionId = link.dataset.questionId;
    if (!setId) return;

    const contents = document.querySelectorAll('.chat-tab-content');
    contents.forEach(c => c.classList.remove('active'));
    document.getElementById('tab-answers')?.classList.add('active');
    history.replaceState(null, '', `#/chat/${chatId}/answers/${setId}`);

    answersTabLoaded = true;
    loadAnswersTab(otherUserId, otherUserName, setId, questionId || undefined);
  });
}

function setupInputHandlers(): void {
  const input = document.getElementById('message-input') as HTMLInputElement;
  const sendBtn = document.getElementById('send-btn') as HTMLButtonElement;
  const photoBtn = document.getElementById('photo-btn') as HTMLButtonElement;

  const state = store.getState();
  const liveTypingEnabled = state.user?.preferences?.live_typing_enabled ?? true;

  const clearTypingTimeout = () => {
    if (typingTimeoutId) {
      clearTimeout(typingTimeoutId);
      typingTimeoutId = null;
    }
  };

  const resetTypingTimeout = () => {
    clearTypingTimeout();
    typingTimeoutId = window.setTimeout(() => {
      socket.clearTyping(chatId);
    }, TYPING_TIMEOUT_MS);
  };

  input.addEventListener('input', () => {
    sendBtn.disabled = !input.value.trim();

    if (liveTypingEnabled && input.value) {
      socket.sendTyping(chatId, input.value);
      resetTypingTimeout();
    } else if (!input.value) {
      clearTypingTimeout();
      socket.clearTyping(chatId);
    }
  });

  input.addEventListener('blur', () => {
    if (liveTypingEnabled && input.value) {
      clearTypingTimeout();
      socket.clearTyping(chatId);
    }
  });

  input.addEventListener('focus', () => {
    if (liveTypingEnabled && input.value) {
      socket.sendTyping(chatId, input.value);
      resetTypingTimeout();
    }
  });

  const handleSlashCommand = async (command: string): Promise<boolean> => {
    if (command === '/ice') {
      try {
        await chatsApi.requestIcebreaker(chatId);
      } catch (err: any) {
        showApiError(err, API_ERR_ICEBREAKER);
      }
      return true;
    }
    if (command === '/clear') {
      try {
        await chatsApi.clearChat(chatId);
        messages = [];
        renderMessages();
      } catch (err: any) {
        showApiError(err, API_ERR_CLEAR_CHAT);
      }
      return true;
    }
    if (command === '/untrust') {
      try {
        await chatsApi.untrust(chatId);
        showToast('Trust removed for both users', 'success');
      } catch (err: any) {
        showApiError(err, API_ERR_UNTRUST);
      }
      return true;
    }
    return false;
  };

  const sendMessage = async () => {
    const content = input.value.trim();
    if (!content) return;

    clearTypingTimeout();
    sendBtn.disabled = true;
    input.value = '';
    socket.clearTyping(chatId);

    if (await handleSlashCommand(content)) {
      input.focus();
      return;
    }

    // Optimistic: show the bubble immediately
    const tempId = `pending_${Date.now()}`;
    const userId = store.getState().user?.id || '';
    const optimisticMsg: Message = {
      id: tempId,
      chat_id: chatId,
      sender_id: userId,
      content,
      read_at: null,
      created_at: new Date().toISOString(),
      moderation_flagged: null,
      images: [],
    };
    messages.push(optimisticMsg);
    appendMessage(optimisticMsg);

    try {
      await chatsApi.sendMessage(chatId, content);
    } catch (err: any) {
      // Remove optimistic message on failure
      messages = messages.filter(m => m.id !== tempId);
      renderMessages();
      showApiError(err, API_ERR_SEND_MESSAGE);
    } finally {
      sendBtn.disabled = true;
      input.focus();
    }
  };

  sendBtn.addEventListener('click', sendMessage);

  input.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') sendMessage();
  });

  // Photo button opens image picker
  photoBtn.addEventListener('click', () => {
    if (imagePickerOpen) {
      closeImagePicker();
    } else {
      openImagePicker();
    }
  });
}

// ============================================================================
// Image Picker
// ============================================================================

async function openImagePicker(): Promise<void> {
  imagePickerOpen = true;
  selectedMediaIds.clear();

  const picker = document.getElementById('image-picker')!;
  picker.style.display = '';

  // Hide messages and input so picker fills the flex space
  const messagesEl = document.getElementById('messages');
  const inputContainer = document.querySelector('.chat-input-container') as HTMLElement | null;
  if (messagesEl) messagesEl.style.display = 'none';
  if (inputContainer) inputContainer.style.display = 'none';

  picker.innerHTML = `
    <div class="chat-image-picker-body">
      ${renderUploadButton('picker-upload-btn')}
      <div class="settings-actions">
        <button class="btn btn-primary" id="picker-send-top-btn" disabled>Send Photos</button>
      </div>
      <div class="chat-image-picker-grid" id="picker-grid">
        <div class="chat-image-picker-loading">${loadingSpinner()}</div>
      </div>
      <div class="settings-actions">
        <button class="btn btn-primary" id="picker-send-bottom-btn" disabled>Send Photos</button>
        <button class="btn btn-danger-outline" id="picker-delete-btn" disabled>Delete Photos</button>
      </div>
    </div>
  `;

  // Upload button (click + drag-drop)
  pickerUploadCleanup = setupUploadButton('picker-upload-btn', handleFileUploads);

  // Send buttons (top + bottom)
  document.getElementById('picker-send-top-btn')!.addEventListener('click', sendSelectedImages);
  document.getElementById('picker-send-bottom-btn')!.addEventListener('click', sendSelectedImages);

  // Delete button
  document.getElementById('picker-delete-btn')!.addEventListener('click', deleteSelectedImages);

  // Load previously-sent image library
  try {
    const res = await chatsApi.getSentImages(chatId);
    sentImages = res.data;
    renderPickerGrid();
  } catch {
    const grid = document.getElementById('picker-grid')!;
    grid.innerHTML = '<div class="empty-state"><p>Could not load images</p></div>';
  }
}

function closeImagePicker(): void {
  imagePickerOpen = false;
  selectedMediaIds.clear();
  if (pickerUploadCleanup) {
    pickerUploadCleanup();
    pickerUploadCleanup = undefined;
  }
  const picker = document.getElementById('image-picker');
  if (picker) {
    picker.style.display = 'none';
    picker.innerHTML = '';
  }
  // Restore messages and input, scroll to bottom
  const messagesEl = document.getElementById('messages');
  const inputContainer = document.querySelector('.chat-input-container') as HTMLElement | null;
  if (messagesEl) {
    messagesEl.style.display = '';
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }
  if (inputContainer) inputContainer.style.display = '';
}

function renderPickerGrid(): void {
  const grid = document.getElementById('picker-grid');
  if (!grid) return;

  if (sentImages.length === 0) {
    grid.innerHTML = '';
    updatePickerActions();
    return;
  }

  grid.innerHTML = sentImages.map(img => {
    const selected = selectedMediaIds.has(img.media_id);
    return `<div class="chat-image-picker-tile ${selected ? 'selected' : ''}" data-media-id="${img.media_id}">
      <img src="${img.thumb_url}" alt="Sent image" />
      <div class="chat-image-picker-check"><i class="fas fa-times"></i></div>
    </div>`;
  }).join('');

  // Tile click -> toggle selection
  grid.querySelectorAll('.chat-image-picker-tile').forEach(tile => {
    tile.addEventListener('click', () => {
      const mediaId = (tile as HTMLElement).dataset.mediaId!;
      if (selectedMediaIds.has(mediaId)) {
        selectedMediaIds.delete(mediaId);
        tile.classList.remove('selected');
      } else {
        selectedMediaIds.add(mediaId);
        tile.classList.add('selected');
      }
      updatePickerActions();
    });
  });

  updatePickerActions();
}

function updatePickerActions(): void {
  const sendTopBtn = document.getElementById('picker-send-top-btn') as HTMLButtonElement | null;
  const sendBottomBtn = document.getElementById('picker-send-bottom-btn') as HTMLButtonElement | null;
  const deleteBtn = document.getElementById('picker-delete-btn') as HTMLButtonElement | null;

  const count = selectedMediaIds.size;
  const hasSelection = count > 0;
  const sendLabel = hasSelection ? (count === 1 ? 'Send 1 Photo' : `Send ${count} Photos`) : 'Send Photos';
  const deleteLabel = hasSelection ? (count === 1 ? 'Delete 1 Photo' : `Delete ${count} Photos`) : 'Delete Photos';

  if (sendTopBtn) { sendTopBtn.disabled = !hasSelection; sendTopBtn.textContent = sendLabel; }
  if (sendBottomBtn) { sendBottomBtn.disabled = !hasSelection; sendBottomBtn.textContent = sendLabel; }
  if (deleteBtn) { deleteBtn.disabled = !hasSelection; deleteBtn.textContent = deleteLabel; }
}

async function sendSelectedImages(): Promise<void> {
  if (selectedMediaIds.size === 0) return;
  const mediaIds = Array.from(selectedMediaIds);
  try {
    await chatsApi.sendMessage(chatId, undefined, mediaIds);
    closeImagePicker();
  } catch (err: any) {
    showApiError(err, API_ERR_SEND_MESSAGE);
  }
}

async function deleteSelectedImages(): Promise<void> {
  if (selectedMediaIds.size === 0) return;
  const mediaIds = Array.from(selectedMediaIds);
  const count = mediaIds.length;

  try {
    await chatsApi.deleteChatImages(chatId, mediaIds);
    sentImages = sentImages.filter(img => !selectedMediaIds.has(img.media_id));
    selectedMediaIds.clear();
    renderPickerGrid();
    showToast(`${count} photo${count === 1 ? '' : 's'} deleted`, 'success');
  } catch (err: any) {
    showApiError(err, API_ERR_DELETE_PHOTOS);
  }
}

async function handleFileUploads(files: File[]): Promise<void> {
  const grid = document.getElementById('picker-grid');
  const total = files.length;
  const progressHtml = `
    <div class="chat-image-picker-uploading" id="picker-progress">
      <div class="upload-progress-label">Uploading 0 / ${total}</div>
      <div class="upload-progress-track"><div class="upload-progress-fill" style="width:0%"></div></div>
    </div>`;
  if (grid) grid.insertAdjacentHTML('afterbegin', progressHtml);

  const mediaIds: string[] = [];
  let completed = 0;

  const updateProgress = () => {
    const label = document.querySelector('#picker-progress .upload-progress-label');
    const fill = document.querySelector('#picker-progress .upload-progress-fill') as HTMLElement | null;
    if (label) label.textContent = `Uploading ${completed} / ${total}`;
    if (fill) fill.style.width = `${Math.round((completed / total) * 100)}%`;
  };

  for (const file of files) {
    try {
      const res = await chatsApi.uploadChatImage(chatId, file);
      mediaIds.push(res.data.mediaId);

      // Add to sent images cache
      sentImages.unshift({
        media_id: res.data.mediaId,
        thumb_url: res.data.thumbUrl,
        large_url: res.data.largeUrl,
        created_at: new Date().toISOString(),
      });
    } catch (err: any) {
      showToast(TOAST_FAILED_UPLOAD_IMAGE, 'error');
    }
    completed++;
    updateProgress();
  }

  // Remove progress indicator
  if (grid) {
    const progressEl = document.getElementById('picker-progress');
    if (progressEl) progressEl.remove();
  }

  if (mediaIds.length === 0) return;

  // Send immediately
  try {
    await chatsApi.sendMessage(chatId, undefined, mediaIds);
    closeImagePicker();
  } catch (err: any) {
    showApiError(err, API_ERR_SEND_MESSAGE);
    renderPickerGrid();
  }
}

// Cleanup when leaving chat
window.addEventListener('hashchange', () => {
  if (chatId && !getCurrentPath().startsWith('/chat/')) {
    if (typingTimeoutId) {
      clearTimeout(typingTimeoutId);
      typingTimeoutId = null;
    }
    if (aiThinkingTimeoutId) {
      clearTimeout(aiThinkingTimeoutId);
      aiThinkingTimeoutId = null;
    }
    if (aiUploadingTimeoutId) {
      clearTimeout(aiUploadingTimeoutId);
      aiUploadingTimeoutId = null;
    }
    aiThinking = false;
    aiThinkingUserId = '';
    aiUploading = false;
    socket.leaveChat(chatId);
    socket.clearTyping(chatId);
    chatId = '';
    cleanupAnswersTab();
    answersTabLoaded = false;
    if (imagePickerOpen) closeImagePicker();
  }
});
